import { createSelector } from 'reselect';
import { CheckoutState } from '../reducers';
import {
	AvailableGdsServices,
	convertBaggageObject,
	convertMealObject,
	GdsAggregationSelectedService,
	GdsAggregationService,
	GdsAggregationServiceGroup,
	GdsAggregationServiceSeat,
	GdsAggregationServiceType,
	GdsServicesAggregations,
	Segment,
	SegmentValue,
	TravellerValue
} from '../../types';
import { Segment as BagSegment } from '../../../Baggage/types';
import { Meal } from '../../../Meal/types';
import { Baggage, SelectedBaggageItem } from '../../../BaggageService/types';
import {
	buildGdsServicesById,
	buildSelectedGdsServiceKeys,
	filterIncludedBaggageSegmentsByReplacedBy,
	getIncludedBaggageWeightValue,
	getMiscServiceGroups as getMiscServiceGroupsFn
} from '@shared/lib/gdsService';
import {
	getDefaultTimelineStep,
	getGdsServiceMinPrice,
	getIsBrandIncludedService,
	getServicePrice,
	isPetBaggage
} from '../../utils';
import { parseIncludedBaggage } from '../../../BaggagePage/utils';
import { addMoney, getTotalPrice } from '@shared/lib/money';
import {
	getFlatArraySet,
	getHasRequestedServices,
	getSeatMapUniqueAvailableSegments,
	getSelectedInsurancesByTypes,
	isOdd
} from '../../../utils';
import { getImportantInsuranceCodesByAvailableCodes } from '../../../InsurancePage/utils';
import {
	getHasSelectedSeats,
	getPassengerStateCompatible,
	getSelectedServicesState
} from '../selectedServices/selectors';
import { useConfig } from '../../../context';
import {
	AviaPassengerType,
	BaggageType,
	ExareStatus,
	FareFamilyFragment,
	FlightSegmentStatus,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceBaseInfo_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsServiceFragment,
	GdsServiceVipSubType,
	InsuranceProgramFragment,
	InsuranceType,
	InsuranceProgramGroupFragment,
	OrderAdditionalServiceGdsServiceBaggage,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderStatus,
	PassengerFragment,
	PaymentStatusEnum,
	SearchParametersFragment,
	SegmentFragment,
	TimelineStep,
	TravellerCardFfpInfo,
	TravellerFieldEnum,
	VipServiceFragment,
	NASSelectedProductFragment,
	NASProductFragment,
	NASStatus
} from '@websky/graphql';
import { getRelatedSegments } from '../../../Baggage/UpgradeBaggageDialog/utils';
import { SelectedServicesState } from '../selectedServices/reducers';
import { SegmentId } from '../../../types';
import { TravellerWithServices } from '../../../CheckoutAdditionalService/components/types';
import { BUSINESS_RULES_CHECKBOXES_CODES } from '@shared/config/constants/app';
import { getTravellerContactInfoValues } from '@shared/lib/passengers';
import { getAggregatorGroupType } from '@entities/order/service';

export const getSelectedTravellerServices = (
	gdsServices: GdsServiceFragment[],
	travellers: TravellerWithServices[],
	confirmationStatus: 'confirmed' | 'notConfirmed' | 'all' = 'notConfirmed'
): GdsServiceFragment[] => {
	const selectedServices: GdsServiceFragment[] = [];

	gdsServices.forEach(service => {
		const allowedPassengerIdSet = getFlatArraySet(service.allowedPassengers);

		if (allowedPassengerIdSet.size) {
			travellers.forEach(traveller => {
				if (allowedPassengerIdSet.has(traveller.id)) {
					const allowedSegmentIdSet = getFlatArraySet(service.allowedSegments);

					if (allowedSegmentIdSet.size) {
						traveller.services?.gdsServices?.services?.forEach(selectedService => {
							if (
								selectedService.serviceId === service.id &&
								selectedService.segmentIds.every(id => allowedSegmentIdSet.has(id)) &&
								(confirmationStatus === 'confirmed'
									? selectedService.confirmedCount > 0
									: confirmationStatus === 'notConfirmed'
									? selectedService.confirmedCount === 0
									: true)
							) {
								selectedServices.push({
									...service,
									price: {
										...service.price,
										amount: service.price.amount * (selectedService.segmentIds?.length || 1)
									}
								});
							}
						});
					}
				}
			});
		}
	});

	return selectedServices;
};

export const getSelectedServicesTotalPrice = (services: GdsServiceFragment[]) => {
	if (!services.length) {
		return null;
	}

	const price = getTotalPrice(services, service => service.price);

	if (price.amount === 0) {
		return null;
	}

	return price;
};

const getState = (state: CheckoutState) => state;

export const getOrder = createSelector(getState, state => state.order);

export const getShowServicesBeforeBooking = createSelector(getOrder, order => order.showServicesBeforeBooking ?? true);

export const getPaymentStatus = createSelector(getOrder, order => order.paymentStatus);

export const getIsBlockedForPayment = createSelector(getOrder, order => order.isBlockedForPayment);

export const getFlight = createSelector(getOrder, order => order.flight);

export const getSegmentsValues = createSelector(getFlight, flight =>
	flight.segmentGroups.map<SegmentValue>(segment => {
		const firstDeparture = segment.segments[0].departure;
		const lastArrival = segment.segments[segment.segments.length - 1].arrival;

		const departureCityName = firstDeparture.airport.city.name;
		const arrivalCityName = lastArrival.airport.city.name;

		return {
			id: segment.groupId,
			label: [departureCityName, arrivalCityName].join(' — '),
			departure: {
				iata: firstDeparture.airport.iata,
				cityName: departureCityName
			},
			arrival: {
				iata: lastArrival.airport.iata,
				cityName: arrivalCityName
			},
			date: new Date(firstDeparture.date)
		};
	})
);

export const getCanceledFlights = createSelector(getFlight, flight =>
	flight.segmentGroups.filter(group => {
		return group.segments.some(segment => segment.status === FlightSegmentStatus.Canceled);
	})
);

export const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);

export const getServices = createSelector(getOrder, order => order.additionalServices.gdsServices?.services ?? []);

export const getGdsServicesById = createSelector(getServices, buildGdsServicesById);

export const getEveryPortServices = createSelector(getOrder, order => order.additionalServices.nas);

export const getEveryPortServicesToRefund = createSelector(getEveryPortServices, services => {
	const selectedServices: (NASSelectedProductFragment & {
		product: NASProductFragment;
	})[] = [];

	const nasServicesMap = new Map(services?.services?.map(service => [service.id, service]) ?? []);

	services?.selectedServices?.forEach(selectedService => {
		const nasService = nasServicesMap.get(selectedService.product.id);

		if (
			nasService &&
			selectedService.orders.some(order => order.isRefundable && order.status === NASStatus.Confirmed)
		) {
			selectedServices.push({ ...selectedService, product: nasService });
		}
	});

	return selectedServices;
});

export const getIsShowRefundNasServices = createSelector(getEveryPortServicesToRefund, services => !!services.length);

export const getRecheckAvailabilityTimout = createSelector(getOrder, order => order.timeoutToReCheckAvailability);
export const getActualizationResult = createSelector(getOrder, order => order.actualizationResult);

export const getIsNeedToHideFlightPrice = createSelector(
	getOrder,
	order => (order.hideItFares || false) && [OrderStatus.Confirmed, OrderStatus.Cancelled].includes(order.status)
);

export const getTravellers = createSelector(getOrder, order => order.travellers);

export const getIsFfpSupported = createSelector(getOrder, order => order.ffpInfo?.ffpSupported);

export const getTravellersValues = createSelector(getTravellers, travellers =>
	travellers.map<TravellerValue>(traveller => ({
		id: traveller.id,
		type: traveller.type,
		supplierTravellerType: traveller.supplierTravellerType,
		linkedTraveller: traveller.linkedTraveller
			? {
					id: traveller.linkedTraveller.id,
					type: traveller.linkedTraveller.type,
					values: getTravellerContactInfoValues(traveller.linkedTraveller.values)
			  }
			: null,
		values: getTravellerContactInfoValues(traveller.values)
	}))
);

export const getExareInfo = createSelector(getOrder, order => order.exareInfo);

export const getLastExchange = createSelector(getExareInfo, exareInfo => exareInfo?.lastExchange);
export const getLastRefund = createSelector(getExareInfo, exareInfo => exareInfo?.lastRefund);
export const getLastGdsServicesRefund = createSelector(getExareInfo, exareInfo => exareInfo?.lastGdsServicesRefund);
export const getGdsServicesRefundAvailability = createSelector(
	getExareInfo,
	exareInfo => !!exareInfo?.gdsServicesRefundAvailability
);

export const getIsGdsServicesRefundAutoMode = createSelector(
	getExareInfo,
	exareInfo => !!exareInfo?.gdsServicesRefundAutoMode
);

export const getExchangeAvailability = createSelector(getExareInfo, exareInfo => !!exareInfo?.exchangeAvailability);

export const getExchangeRequestRefuseAllowed = createSelector(
	getLastExchange,
	lastExchange => !!lastExchange?.refuseAllowed
);

export const getOrderLastExchangeStatus = createSelector(getLastExchange, lastExchange => lastExchange?.status);

export const getIsOpenedLastExchange = createSelector(
	getOrderLastExchangeStatus,
	status => status && [ExareStatus.InProcess, ExareStatus.AwaitingPayment, ExareStatus.NeedConfirm].includes(status)
);

export const getIsAwaitingPaymentExchange = createSelector(
	getOrderLastExchangeStatus,
	status => status && status === ExareStatus.AwaitingPayment
);

export const getIsExchangeAutoMode = createSelector(getExareInfo, exareInfo => !!exareInfo?.exchangeAutoMode);

export const getisAutoReturnEmdPD = createSelector(getExareInfo, exareInfo => exareInfo?.isAutoReturnEmdPD ?? false);

export const getLastExchangeFlights = createSelector(getLastExchange, lastExchange => {
	if (!lastExchange) {
		return null;
	}

	return (lastExchange.flight?.segments || [])
		.filter(segment => segment.segment.status !== FlightSegmentStatus.Confirmed)
		.map(segment => ({
			...lastExchange.flight,
			segments: [segment]
		}));
});

export const getBonusForFlight = createSelector(getOrder, order => order.ffpInfo?.bonusForFlight);
export const getPriceInMiles = createSelector(getOrder, order => order.price?.totalInMiles);

export const getCustomerSubscribed = createSelector(getOrder, order => order.customer.subscribed);

export const getAvailableServices = createSelector(getServices, services => {
	const availableGdsServices: AvailableGdsServices = {};

	services.forEach(service => {
		const serviceType =
			service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
				? (service as OrderAdditionalServiceGdsServiceBaggage).baggageType
				: service.type;

		if (!availableGdsServices.hasOwnProperty(serviceType)) {
			availableGdsServices[serviceType] = serviceType;
		}
	});

	return availableGdsServices;
});

export const getSegmentGroups = createSelector(getOrder, order => order.flight?.segmentGroups);

export const getFareFamiliesMap = createSelector(getSegmentGroups, segmentGroups => {
	const fareFamiliesMap = new Map<string, { fareFamily: FareFamilyFragment; segmentIds: string[] }>();

	segmentGroups.forEach(segmentGroup => {
		const fareFamily = segmentGroup.fareFamily;

		if (!fareFamily) {
			return;
		}

		if (!fareFamiliesMap.has(fareFamily.id)) {
			fareFamiliesMap.set(fareFamily.id, {
				fareFamily,
				segmentIds: []
			});
		}

		segmentGroup.segments.forEach(segment => fareFamiliesMap.get(fareFamily.id).segmentIds.push(segment.id));
	});

	return fareFamiliesMap;
});

export const getIsOrderBlockedForPayment = createSelector(getOrder, order => order.isBlockedForPayment);

export const getOldOrderPrice = createSelector(getOrder, order => order?.actualizationResult?.oldPrice);
export const getNewOrderPrice = createSelector(getOrder, order => order?.actualizationResult?.newPrice);

export const getFareLockService = createSelector(getServices, services =>
	services.find(service => service.isPriceFreeze)
);

export const getIsFareLockSelected = createSelector(getTravellers, getFareLockService, (travellers, fareLockService) =>
	travellers?.some(traveller =>
		traveller?.services?.gdsServices?.services?.some(service => service?.serviceId === fareLockService?.id)
	)
);

export const getIsFareLockConfirmed = createSelector(getTravellers, getFareLockService, (travellers, fareLockService) =>
	travellers?.some(traveller =>
		traveller?.services?.gdsServices?.services?.some(
			service => service?.serviceId === fareLockService?.id && service?.confirmedCount
		)
	)
);

export const getIsPromoCodeAppliedToFlight = createSelector(
	getOldOrderPrice,
	getNewOrderPrice,
	(oldPrice, newPrice) => newPrice?.amount < oldPrice?.amount
);

const _getIncludedBaggage = createSelector(
	getFareFamiliesMap,
	getTravellers,
	useConfig,
	(fareFamiliesMap, travellers, config) => {
		if (['N4', 'DV', 'YC'].includes(config?.global?.companyInfo?.iataCode)) {
			// filter out all included baggage for INF and INS
			const travellersWithoutInfants = travellers.filter(
				traveller => traveller.type !== AviaPassengerType.INF && traveller.type !== AviaPassengerType.INS
			);

			return fareFamiliesMap
				? parseIncludedBaggage(
						fareFamiliesMap,
						travellersWithoutInfants.map(({ id }) => id)
				  )
				: [];
		}

		return fareFamiliesMap
			? parseIncludedBaggage(
					fareFamiliesMap,
					travellers.map(({ id }) => id)
			  )
			: [];
	}
);

export const getBrandIncludedBaggage = createSelector(getTravellers, travellers => {
	const includedBaggages: SelectedBaggageItem[] = [];

	travellers.forEach(traveller =>
		traveller.services?.brandIncludedServices?.services
			.filter(services => services.service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage)
			.forEach(service => {
				const baggageService = service.service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;

				const baggage: Baggage = {
					id: service.serviceId,
					segmentIds: baggageService.allowedSegments,
					includedSegments: service.segmentIds,
					type: baggageService.baggageType,
					name: baggageService.name,
					description: baggageService.description,
					value: getIncludedBaggageWeightValue(baggageService.baggageWeight),
					size: baggageService.size,
					included: service.confirmedCount,
					price: baggageService.price,
					rfics: baggageService.rfisc,
					canBeAdded: false,
					allowedPassengers: [traveller.id],
					isReplacedBy: baggageService.isReplacedBy
				};

				includedBaggages.push({
					...service,
					passengerId: traveller.id,
					baggage
				});
			})
	);

	return includedBaggages;
});

/* WEBSKY-8550: Включенный багаж, с отфильтрованными сегментами, в которых заменен выбранной доп-услугой (rfisc есть в `isReplacedBy`). */
export const getReplacedBrandIncludedBaggage = createSelector(
	[getBrandIncludedBaggage, getTravellers, getServices],
	(includedBaggage, travellers, services) => {
		const selectedKeys = buildSelectedGdsServiceKeys(travellers, buildGdsServicesById(services));

		return filterIncludedBaggageSegmentsByReplacedBy(includedBaggage, selectedKeys);
	}
);

export const getMiscServices = createSelector(
	getServices,
	services =>
		services.filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Misc
		) as GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment[]
);

export const getMiscServiceGroups = createSelector(getServices, getMiscServiceGroupsFn);

export const getAnimalServices = createSelector(
	getServices,
	services =>
		services.filter(service =>
			[BaggageType.PetInHandCarrier].includes(
				(service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment).baggageType
			)
		) as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[]
);

export const getSelectedTravellerAnimalServices = createSelector(
	getAnimalServices,
	getTravellers,
	getSelectedTravellerServices
);

export const getSelectedAnimalServicesPrice = createSelector(
	getSelectedTravellerAnimalServices,
	getSelectedServicesTotalPrice
);

export const hasSeatsServices = createSelector(getServices, services =>
	services.some(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Seat)
);

export const hasBaggageServices = createSelector(getServices, services =>
	services.some(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage)
);

export const getSeatMap = createSelector(getOrder, order => order.flight.seatMap);
export const getOrderId = createSelector(getOrder, order => order.id);
export const getOrderHash = createSelector(getOrder, order => order.stateHash);
export const getIsOddOrderId = createSelector(getOrderId, isOdd);
export const getIsNew = createSelector(getOrder, order => order.status === OrderStatus.New);
export const getIsNotNew = createSelector(getOrder, order => order.status !== OrderStatus.New);
export const getIsTravellersDataFilled = createSelector(getOrder, order => order.isTravellersDataFilled);
export const getIsReadonly = createSelector(
	getIsTravellersDataFilled,
	getIsNew,
	(isTravellersDataFilled, isNew) => isTravellersDataFilled && !isNew
);
export const getIsTravellersDataCompletionRequired = createSelector(
	getIsTravellersDataFilled,
	getIsNew,
	(isTravellersDataFilled, isNew) => !isTravellersDataFilled && !isNew
);
export const getIsRedirectFromMetasearch = createSelector(getOrder, order => order.redirectFromMetasearch);
export const getOrderStatus = createSelector(getOrder, order => order.status);

export const getHasAvailableSeats = createSelector(getSeatMap, seatMap =>
	seatMap?.segments?.some(({ isAnySeatExistsAndFree }) => isAnySeatExistsAndFree)
);

export const getIsAllSegmentsAvailableOnlyOnCheckin = createSelector(getSeatMap, seatMap =>
	seatMap.segments.every(segment => segment.isSeatsAvailableInCheckinOnly === true)
);

export const getIsBookedOrConfirmed = createSelector(
	getOrder,
	order => order.status === OrderStatus.Booked || order.status === OrderStatus.Confirmed
);

export const getTotalInsurancePrice = createSelector(getOrder, state => state.price.insurance);

export const getIsBooked = createSelector(getOrder, order => order.status === OrderStatus.Booked);

export const getIsConfirmed = createSelector(getOrder, order => order.status === OrderStatus.Confirmed);

export const getIsConfirmedOrCancelled = createSelector(
	getOrder,
	order => order.status === OrderStatus.Confirmed || order.status === OrderStatus.Cancelled
);

export const getIsPriceFreezeProductSelected = createSelector(getOrder, order => order.isPriceFreezeProductSelected);
export const getIsCancelled = createSelector(getOrder, order => order.status === OrderStatus.Cancelled);

export const getIsFFPUsed = createSelector(getOrder, order => !!order.ffpInfo);
export const getMilesForOrder = createSelector(getOrder, order => order.ffpInfo?.bonusForFlight);

// comment for next php release
export const getFfpBonusesForFlightByTravellers = createSelector(getOrder, order => {
	const orderTravellersById: { [key: string]: TravellerCardFfpInfo } = {};

	for (const traveller of order.travellers) {
		orderTravellersById[traveller.id] = null;
	}

	order.ffpInfo?.bonusByCard?.forEach(travellerBonus => {
		const orderTraveller = order.travellers.find(traveller => {
			return traveller.values.some(
				value => value.type === TravellerFieldEnum.LoyaltyNumber && value.value === travellerBonus.cardNumber
			);
		});

		if (orderTraveller) {
			orderTravellersById[orderTraveller.id] = {
				...travellerBonus,
				cardNumber: travellerBonus.cardNumber
			};
		}
	});

	return orderTravellersById;
});

export const getAvailableGdsServicesByType = createSelector(
	getOrder,
	getSeatMap,
	getHasSelectedSeats,
	(order, seatMap, hasSelectedSeats) => {
		const availableServices: { [key in OrderAdditionalServiceGdsServiceServiceType]?: boolean } = {};

		order.additionalServices?.gdsServices?.services?.forEach(service => {
			// TODO: check that service.canBeAdded = true when php fix it
			if (!availableServices.hasOwnProperty(service.type)) {
				availableServices[service.type] = true;
			}
		});

		const hasPassengersAvailableForSeatSelection = order.travellers?.some(
			traveller =>
				traveller.type === AviaPassengerType.ADT ||
				traveller.type === AviaPassengerType.CLD ||
				traveller.type === AviaPassengerType.INS
		);

		return {
			...availableServices,
			...{
				[OrderAdditionalServiceGdsServiceServiceType.Seat]:
					(hasPassengersAvailableForSeatSelection &&
						seatMap?.segments?.some(
							segment => segment.availableSeatsCount > 0 || segment.isSeatsAvailableInCheckinOnly
						)) ||
					hasSelectedSeats
			},
			...{
				[OrderAdditionalServiceGdsServiceServiceType.Baggage]:
					availableServices.Baggage ||
					getIsBrandIncludedService(order, OrderAdditionalServiceGdsServiceServiceType.Baggage)
			},
			...{
				[OrderAdditionalServiceGdsServiceServiceType.Meal]:
					availableServices.Meal ||
					getIsBrandIncludedService(order, OrderAdditionalServiceGdsServiceServiceType.Meal)
			}
		};
	}
);

export const getPaymentIsRequired = createSelector(getOrder, order => order.paymentStatus !== PaymentStatusEnum.Full);
export const getCustomer = createSelector(getOrder, order => order?.customer);
export const getIsContactsInfoIsEmpty = createSelector(getCustomer, customer => customer.contactInfoIsEmpty);

export const isRoundTrip = createSelector(getOrder, (order): boolean => {
	const segmentGroups = order.flight?.segmentGroups;

	if (segmentGroups.length !== 2) {
		return false;
	}

	const outbound = segmentGroups[0];
	const outboundDeparture = outbound.segments[0].departure.airport.iata;
	const outboundArrival = outbound.segments[outbound.segments.length - 1].arrival.airport.iata;

	const inbound = segmentGroups[1];
	const inboundDeparture = inbound.segments[0].departure.airport.iata;
	const inboundArrival = inbound.segments[inbound.segments.length - 1].arrival.airport.iata;

	return outboundArrival === inboundDeparture && outboundDeparture === inboundArrival;
});

export const getSegmentsForServices = createSelector(getOrder, (order): Segment[] => {
	const segments: Segment[] = [];

	order.flight.segmentGroups.forEach(group =>
		group.segments.forEach(segment => {
			segments.push({
				id: segment.id,
				arrival: segment.arrival,
				departure: segment.departure,
				amenities: segment.flightInfo?.amenities,
				groupId: group.groupId
			});
		})
	);

	return segments;
});

export const getSeatMapAvailableSegments = createSelector(getSeatMap, seatMap =>
	getSeatMapUniqueAvailableSegments(seatMap.segments)
);

export const getSegmentsForBaggage = createSelector(getOrder, (order): BagSegment[] => {
	return order.flight.segments.map(seg => ({
		id: seg.segment.id,
		departure: seg.segment.departure,
		arrival: seg.segment.arrival
	}));
});

export const getSegments = createSelector(getOrder, (order): SegmentFragment[] =>
	order.flight.segments.map(segment => segment.segment)
);

export const getSegmentIds = createSelector(getOrder, order =>
	order.flight.segments.map(segment => segment.segment.id)
);

export const getMessages = createSelector(getOrder, order => order.messages);
export const getBusinessRulesCheckboxMessages = createSelector(getMessages, messages =>
	messages.filter(({ code }) => BUSINESS_RULES_CHECKBOXES_CODES.includes(code))
);

export const getBaggageMinPrice = createSelector(getOrder, order =>
	getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage)
);

export const getGroundTransportationServices = createSelector(getServices, services => {
	return services.filter(
		service =>
			service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
			(service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType ===
				GdsServiceVipSubType.GroundTransportation
	) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[];
});

export const getSelectedTravellerGroundTransportationServices = createSelector(
	getGroundTransportationServices,
	getTravellers,
	getSelectedTravellerServices
);

export const getSelectedGroundTransportationServicesPrice = createSelector(
	getSelectedTravellerGroundTransportationServices,
	getSelectedServicesTotalPrice
);

export const getUpgradeBaggageMinPrice = createSelector(getOrder, order =>
	getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage, [BaggageType.HandLuggageExcess])
);

const memoizeByBoolean = <T>(factory: (flag: boolean) => T) => {
	const cache = new Map<boolean, T>();

	return (flag = false): T => {
		let result = cache.get(flag);

		if (result === undefined) {
			result = factory(flag);
			cache.set(flag, result);
		}

		return result;
	};
};

const getSpecialEquipmentBaggageTypes = (showAnimalInSeparateCard: boolean): BaggageType[] =>
	[
		!showAnimalInSeparateCard && BaggageType.PetInHandCarrier,
		BaggageType.PetInCabin,
		BaggageType.PetInHold,
		BaggageType.SportingEquipment,
		BaggageType.MusicalInstrument,
		BaggageType.FishingEquipment,
		BaggageType.SurfingEquipment,
		BaggageType.Ski,
		BaggageType.Bike
	].filter(Boolean);

export const getSpecialEquipmentMinPrice = memoizeByBoolean((showAnimalInSeparateCard: boolean) =>
	createSelector(getOrder, order =>
		getGdsServiceMinPrice(
			order,
			OrderAdditionalServiceGdsServiceServiceType.Baggage,
			getSpecialEquipmentBaggageTypes(showAnimalInSeparateCard)
		)
	)
);

export const getOverweightMinPrice = createSelector(getOrder, order =>
	getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage, [BaggageType.BaggageExcess])
);

export const getCurrencyFromOrder = createSelector(getOrder, order => order.price?.total?.currency);

export const getSeatsMinPrice = createSelector(getOrder, order => {
	let minPrice = 0;
	const currency = order.additionalServices.gdsServices?.services?.find(service => !!service?.price?.currency)?.price
		.currency;

	order.flight.seatMap?.segments?.forEach(segment => {
		segment?.decks?.forEach(deck => {
			deck?.rows?.forEach(row => {
				row?.seats.forEach(seat => {
					if (!seat?.isAvailable) {
						return;
					}

					if (!minPrice) {
						minPrice = seat?.price?.amount;
					}

					if (seat?.price?.amount < minPrice) {
						minPrice = seat.price.amount;
					}
				});
			});
		});
	});

	return {
		currency,
		amount: minPrice
	};
});

export const getMealMinPrice = createSelector(getOrder, order =>
	getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Meal)
);

// #todo: add tests
export const getSearchParameters = createSelector(
	getOrder,
	getCurrencyFromOrder,
	({ flight, travellers }, orderCurrency): SearchParametersFragment => {
		const passengersMap: { [passType: string]: number } = {};
		const passengers: SearchParametersFragment['passengers'] = [];

		travellers.forEach(({ type }) => {
			if (passengersMap.hasOwnProperty(type)) {
				passengersMap[type]++;
			} else {
				passengersMap[type] = 1;
			}
		});

		for (const passengerType in passengersMap) {
			if (passengersMap.hasOwnProperty(passengerType)) {
				passengers.push({
					passengerType: passengerType as AviaPassengerType,
					extendedPassengerType: null,
					count: passengersMap[passengerType]
				});
			}
		}

		return {
			promotionCode: null,
			currency: orderCurrency,
			passengers,
			segments: flight.segmentGroups.map<SearchParametersFragment['segments'][0]>(group => {
				const firstSegment = group.segments[0];
				const lastSegment = group.segments[group.segments.length - 1];

				return {
					date: firstSegment.departure.date,
					departure: {
						name: firstSegment.departure.airport.name,
						iata: firstSegment.departure.airport.iata,
						city: {
							name: firstSegment.departure.airport.city.name
						}
					},
					arrival: {
						name: lastSegment.arrival.airport.name,
						iata: lastSegment.arrival.airport.iata,
						city: {
							name: lastSegment.arrival.airport.city.name
						}
					}
				};
			}),
			ffpMode: false
		};
	}
);

// check that canAddServices is boolean ( because in CheckinOrder there is no such props and shouldRedirectToFallback always returns true )
export const shouldRedirectToFallback = createSelector(
	getOrder,
	({ canAddServices, canRemoveServices }) =>
		typeof canAddServices === 'boolean' &&
		typeof canRemoveServices === 'boolean' &&
		(!canAddServices || !canRemoveServices)
);

export const getServicesFallbackURL = createSelector(getOrder, ({ servicesFallbackURL }) => servicesFallbackURL);

export const getAeroexpress = createSelector(getOrder, order => order.additionalServices?.transfer?.aeroexpress);

export const getInsurances = createSelector(getOrder, order => order.additionalServices?.insurances);

export const getInsurancePrograms = createSelector(getInsurances, insurances => insurances?.insurancePrograms ?? []);

export const getCanAddedInsurancePrograms = createSelector(getInsurancePrograms, insurancePrograms =>
	insurancePrograms.filter(program => program.canBeAdded)
);

export const getInsurancesGroups = createSelector(getInsurances, insurances => insurances?.insuranceProgramGroups);

export const getSelectedInsurances = createSelector(getInsurances, insurances => insurances?.selectedInsurances ?? []);

export const getSelectedVskInsurancesByType = createSelector(getSelectedInsurances, selectedInsurances =>
	getSelectedInsurancesByTypes(selectedInsurances, [InsuranceType.Vsk])
);

interface InsuranceGroup {
	groupName: string;
	insurancePrograms: InsuranceProgramFragment[];
}

const getGroupInsurances = (
	programs: InsuranceProgramFragment[] = [],
	groups: InsuranceProgramGroupFragment[] = []
): InsuranceGroup[] => {
	if (!programs.length) {
		return [];
	}

	if (!groups.length) {
		return [{ groupName: null, insurancePrograms: programs }];
	}

	const programMap = new Map(programs.map(program => [program.code, program]));

	return groups
		.map(group => {
			const programsByGroup = group.insurancePrograms.map(({ code }) => programMap.get(code)).filter(Boolean);

			return {
				groupName: group.groupValue,
				insurancePrograms: programsByGroup
			};
		})
		.filter(group => group.insurancePrograms.length);
};

export const getInsurancesByGroups = createSelector(getInsurancePrograms, getInsurancesGroups, getGroupInsurances);

export const getCanBeAddedInsurancesByGroups = createSelector(
	getCanAddedInsurancePrograms,
	getInsurancesGroups,
	getGroupInsurances
);

/** Groups for aggregator Extra: can-be-added programs + already selected (even if canBeAdded=false). */
export const getAggregatorInsurancesByGroups = createSelector(
	getInsurancePrograms,
	getInsurancesGroups,
	getSelectedInsurances,
	(programs, groups, selectedInsurances) => {
		const selectedCodes = new Set(selectedInsurances.map(selected => selected.insuranceProgram.code));

		const visiblePrograms = programs.filter(program => program.canBeAdded || selectedCodes.has(program.code));

		return getGroupInsurances(visiblePrograms, groups);
	}
);

export const getAvailableInsuranceCodes = createSelector(getInsurances, insurance => {
	const availableInsuranceCodeSet = new Set<string>();

	if (insurance?.insurancePrograms?.length) {
		insurance.insurancePrograms.forEach(insurance => availableInsuranceCodeSet.add(insurance.code));
	}

	return [...availableInsuranceCodeSet];
});

export const getSelectedInsuranceCodes = createSelector(getInsurances, insurance => {
	const selectedInsuranceCodeSet = new Set<string>();

	if (insurance?.selectedInsurances?.length) {
		insurance.selectedInsurances.forEach(insurance =>
			selectedInsuranceCodeSet.add(insurance.insuranceProgram.code)
		);
	}

	return [...selectedInsuranceCodeSet];
});

export const getDisplayInsurancePrograms = createSelector(
	getInsurancePrograms,
	getSelectedInsuranceCodes,
	(programs, selectedCodes) => programs.filter(program => program.canBeAdded || selectedCodes.includes(program.code))
);

export const getDisplayInsurancesByGroups = createSelector(
	getDisplayInsurancePrograms,
	getInsurancesGroups,
	getGroupInsurances
);

export const getImportantInsuranceCodes = (availableInsuranceCodes: string[] = []) =>
	createSelector(getInsurances, insurances =>
		getImportantInsuranceCodesByAvailableCodes(insurances, availableInsuranceCodes)
	);

export const getAvailableInsuranceCodesByPrograms = (insurancePrograms?: InsuranceProgramFragment[]) =>
	createSelector(getAvailableInsuranceCodes, availableCodes => {
		if (!insurancePrograms?.length) {
			return availableCodes;
		}

		return availableCodes.filter(code => insurancePrograms.some(program => program.code === code));
	});

export const getAvailableSelectedInsuranceCodes = (availableCodes?: string[], include: boolean = true) =>
	createSelector(getSelectedInsuranceCodes, selectedCodes => {
		if (!availableCodes?.length) {
			return selectedCodes;
		}

		return selectedCodes.filter(code => {
			const isIncludeInsurance = availableCodes.includes(code);

			return include ? isIncludeInsurance : !isIncludeInsurance;
		});
	});

export const getInsuranceCodeBySalePriority = createSelector(getInsurances, insurance => {
	const insuranceCodeBySalePriorityMap = new Map<string, number>();

	if (insurance?.insurancePrograms?.length) {
		insurance.insurancePrograms.forEach(insuranceProgram => {
			const salePriority = insuranceProgram.extraParams?.find(param => param.key === 'salePriority')?.value;

			if (salePriority) {
				insuranceCodeBySalePriorityMap.set(insuranceProgram.code, +salePriority);
			}
		});
	}

	return insuranceCodeBySalePriorityMap;
});

export const getSortedPriorityInsurancePrograms = createSelector(
	getInsurances,
	getInsuranceCodeBySalePriority,
	(insurance, insuranceCodeBySalePriority) => {
		if (!insurance?.insurancePrograms?.length || !insuranceCodeBySalePriority.size) {
			return [];
		}

		return [...insurance.insurancePrograms]
			.filter(insuranceProgram => insuranceCodeBySalePriority.has(insuranceProgram.code))
			.sort((a, b) => {
				const aPriority = insuranceCodeBySalePriority.get(a.code) ?? 0;
				const bPriority = insuranceCodeBySalePriority.get(b.code) ?? 0;

				return bPriority - aPriority;
			});
	}
);

export const getPriorityInsuranceProgram = createSelector(
	(_state: CheckoutState, multiplePriorityInsurances: boolean) => multiplePriorityInsurances,
	getSelectedInsuranceCodes,
	getSortedPriorityInsurancePrograms,
	(multiplePriorityInsurances, selectedInsuranceCodes, sortedPriorityInsurances) => {
		if (multiplePriorityInsurances) {
			return sortedPriorityInsurances.filter(insurance => !selectedInsuranceCodes.includes(insurance.code))[0];
		}

		return sortedPriorityInsurances[0];
	}
);

function singleInsuranceProgramFilter(insuranceProgram: InsuranceProgramFragment, inversion?: boolean) {
	return insuranceProgram.important !== inversion;
}

export const getSingleInsurancePrograms = (inversion = false) =>
	createSelector(
		getInsurances,
		services => services?.insurancePrograms?.filter(i => singleInsuranceProgramFilter(i, inversion)) ?? []
	);

export const getSelectedSingleInsurances = (inversion = false) =>
	createSelector(getInsurances, services =>
		services.selectedInsurances.filter(i => singleInsuranceProgramFilter(i.insuranceProgram, inversion))
	);

export const getMealServices = createSelector(
	getServices,
	(services): GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment[] =>
		services.filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Meal
		) as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment[]
);

export const getBaggageServices = createSelector(
	getServices,
	(services): GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[] =>
		services.filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
		) as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[]
);

export const getVipServices = createSelector(
	getServices,
	(services): GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[] =>
		services.filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Vip
		) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
);

export const getMeals = createSelector(getMealServices, (meals): Meal[] => meals.map(convertMealObject));
export const getBaggage = createSelector(getBaggageServices, (baggage): Baggage[] => baggage.map(convertBaggageObject));

export const getUpgradeClassServices = createSelector(
	getVipServices,
	vipServices => vipServices.filter(service => service.subType === GdsServiceVipSubType.Upgrade) ?? []
);

const isUpgradeClassServiceSelected = (
	upgradeServices: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[],
	travellers: PassengerFragment[],
	segmentId?: string
) => {
	const upgradeServicesIds = upgradeServices.map(({ id }) => id);

	return travellers.some(traveller => {
		return traveller?.services?.gdsServices?.services?.some(service => {
			return (
				upgradeServicesIds.includes(service.serviceId) &&
				(!segmentId || service.segmentIds.includes(segmentId)) &&
				(service.count > 0 || service.confirmedCount > 0)
			);
		});
	});
};

export const getIsUpgradeClassServiceSelected = createSelector(
	getUpgradeClassServices,
	getTravellers,
	isUpgradeClassServiceSelected
);

export const getIsUpgradeClassServiceSelectedBySegment = createSelector(
	getUpgradeClassServices,
	getTravellers,
	(_: CheckoutState, segmentId: string) => segmentId,
	isUpgradeClassServiceSelected
);

export const getIsUpgradeClassSelectedForAllSegments = createSelector(
	getUpgradeClassServices,
	getTravellers,
	getSegments,
	(upgradeServices, travellers, segments) => {
		const upgradeServicesIds = upgradeServices.map(({ id }) => id);

		const upgradeSelectedOnSegments: { [key: SegmentId]: boolean } = {};

		for (const traveller of travellers) {
			const upgradeServices =
				traveller?.services?.gdsServices?.services?.filter(service =>
					upgradeServicesIds.includes(service.serviceId)
				) ?? [];

			for (const upgradeService of upgradeServices) {
				for (const segmentId of upgradeService.segmentIds) {
					if (
						!upgradeSelectedOnSegments[segmentId] &&
						(upgradeService.count > 0 || upgradeService.confirmedCount > 0)
					) {
						upgradeSelectedOnSegments[segmentId] = true;
					}
				}
			}
		}

		return (
			Object.keys(upgradeSelectedOnSegments).length ===
			segments.filter(segment => segment.subStatus === null).length
		);
	}
);

export const getCanAddUpgradeFlightClass = createSelector(
	getUpgradeClassServices,
	upgradeServices => !!upgradeServices?.length
);

export const getBusinessLounge = createSelector(
	getServices,
	services =>
		services.filter(
			service => (service as VipServiceFragment).subType === GdsServiceVipSubType.BusinessLounge
		) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
);

export const getBusinessLoungeByTypes = createSelector(getServices, services => {
	const businessLoungeByTypeMap = new Map<
		GdsServiceVipSubType,
		GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
	>();

	services.forEach(service => {
		const vipService = service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

		if (
			vipService.subType === GdsServiceVipSubType.BusinessLounge ||
			vipService.subType === GdsServiceVipSubType.BusinessLoungeInternational
		) {
			businessLoungeByTypeMap.set(vipService.subType, [
				...(businessLoungeByTypeMap.get(vipService.subType) ?? []),
				vipService
			]);
		}
	});

	return [...businessLoungeByTypeMap.values()];
});

export const getBaggageDelivery = createSelector([getOrder, getServices], (order, services) => {
	return services.reduce<GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]>((services, service) => {
		const newServices = [...services];

		if (
			(service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType ===
			GdsServiceVipSubType.BaggageDelivery
		) {
			const serviceSegments = [...getRelatedSegments(service.allowedSegments)];

			// WEBSKY-2899: сегмент должен быть без пересадки
			serviceSegments
				.filter(
					segmentId =>
						order.flight.segmentGroups.find(group =>
							group.segments.some(segment => segment.id === segmentId)
						)?.segments?.length === 1
				)
				.forEach(segmentId =>
					newServices.push({
						...(service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment),
						allowedSegments: [[segmentId]]
					})
				);
		}

		return newServices;
	}, []);
});

export const getCapsuleHotel = createSelector(
	getServices,
	services =>
		services.filter(
			service => (service as VipServiceFragment).subType === GdsServiceVipSubType.CapsuleHotel
		) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
);

export const getSmsNotificationServices = createSelector(
	getServices,
	services =>
		services.filter(
			service =>
				service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
				(service as VipServiceFragment).subType === GdsServiceVipSubType.SMS
		) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
);

export const getSelectedUpgradeClassServices = createSelector(
	getUpgradeClassServices,
	getTravellers,
	getSelectedTravellerServices
);

export const getSelectedUpgradeClassPrice = createSelector(
	getSelectedUpgradeClassServices,
	getSelectedServicesTotalPrice
);

export const getSelectedTravellerSmsNotifications = createSelector(
	getSmsNotificationServices,
	getTravellers,
	getSelectedTravellerServices
);

export const getSelectedSmsNotificationsPrice = createSelector(
	getSelectedTravellerSmsNotifications,
	getSelectedServicesTotalPrice
);

export const getPriorityBoarding = createSelector(
	getServices,
	services =>
		services.filter(
			service =>
				service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
				(service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType ===
					GdsServiceVipSubType.PriorityBoarding
		) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
);

export const getSelectedTravellerPriorityBoarding = createSelector(
	getPriorityBoarding,
	getTravellers,
	getSelectedTravellerServices
);

export const getRefundableTicketServices = createSelector(
	getServices,
	services =>
		services.filter(
			service =>
				service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
				(service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType ===
					GdsServiceVipSubType.RefundableTicket
		) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]
);

export const getSelectedTravellerRefundableTicket = createSelector(
	getRefundableTicketServices,
	getTravellers,
	getSelectedTravellerServices
);

export const getTravellerRefundableTicket = createSelector(
	getRefundableTicketServices,
	getTravellers,
	(services, travellers) => getSelectedTravellerServices(services, travellers, 'all')
);

export const getSelectedRefundableTicketPrice = createSelector(
	getSelectedTravellerRefundableTicket,
	getSelectedServicesTotalPrice
);

export const getSelectedTravellerBaggageDelivery = createSelector(
	getBaggageDelivery,
	getTravellers,
	getSelectedTravellerServices
);

export const getSelectedPriorityBoardingPrice = createSelector(
	getSelectedTravellerPriorityBoarding,
	getSelectedServicesTotalPrice
);

export const getAdditionalBaggage = createSelector(getBaggage, items => {
	return items.filter(item => item.type === BaggageType.CheckedBaggage);
});

const getFakeBaggage = createSelector(getBaggageServices, (services): Baggage[] => {
	return services.filter(service => service.isFake).map(convertBaggageObject);
});

export const getIncludedBaggage = createSelector(_getIncludedBaggage, getFakeBaggage, (included, fake): Baggage[] => {
	return included.concat(fake);
});

export const getUpgradeBaggage = createSelector(getBaggage, getTravellers, (baggageItems, travellers) => {
	const removableServicesMap = new Map<string, boolean>();

	travellers.forEach(traveller => {
		traveller.services?.gdsServices?.services?.forEach(service => {
			const key = `${traveller.id}/${service.serviceId}`;

			removableServicesMap.set(key, service.count > 0);
		});
	});

	const isCanBeAddedOrRemoved = (item: Baggage) => {
		const canBeRemoved = item.allowedPassengers.some(passengerId =>
			removableServicesMap.get(`${passengerId}/${item.id}`)
		);

		return item.canBeAdded || canBeRemoved;
	};

	return baggageItems.filter(item => {
		return (
			(item.type === BaggageType.HandLuggageExcess || item.type === BaggageType.CarryOn) &&
			isCanBeAddedOrRemoved(item)
		);
	});
});

export const getSpecialEquipment = memoizeByBoolean((showAnimalInSeparateCard = false) =>
	createSelector(getBaggage, items => {
		const types = getSpecialEquipmentBaggageTypes(showAnimalInSeparateCard);

		return items.filter(item => types.includes(item.type));
	})
);

export const getOverweightBaggage = createSelector(getBaggage, items =>
	items.filter(item => item.type === BaggageType.BaggageExcess)
);

export const getUpgradableBaggage = memoizeByBoolean((showAnimalInSeparateCard: boolean) =>
	createSelector(
		[getUpgradeBaggage, getAdditionalBaggage, getSpecialEquipment(showAnimalInSeparateCard), getOverweightBaggage],
		(...baggage) => baggage.reduce((services, items) => [...services, ...items], [])
	)
);

export const getHasEverySelectedSeats = createSelector(getOrder, getPassengerStateCompatible, (order, passengers) => {
	const segmentsCount = order.flight.segments.length;

	return passengers
		.filter(passenger => passenger.type !== AviaPassengerType.INF)
		.some(({ seats = [] }) => {
			const segmentsBySeats = new Set(seats.map(seat => seat.segmentId));

			return seats.length > 0 && segmentsBySeats.size === segmentsCount;
		});
});

export const getHasAnySelectedSeats = createSelector(getPassengerStateCompatible, passengers => {
	return passengers
		.filter(passenger => passenger.type !== AviaPassengerType.INF)
		.some(({ seats = [] }) => {
			return seats.length > 0;
		});
});

export const getSkipRecommendations = createSelector(
	getIsRedirectFromMetasearch,
	getIsOddOrderId,
	(isFromMeta, orderIdIsOdd) => isFromMeta && orderIdIsOdd
);

export const getIsTransferSelected = createSelector(
	getOrder,
	order => order.additionalServices?.transfer?.aeroexpress?.selectedTrips?.length > 0
);

export const getIsInsuranceSelected = createSelector(
	getOrder,
	order => order.additionalServices?.insurances?.selectedInsurances?.length > 0
);

export const getShowSeatRecommendations = createSelector(
	getSkipRecommendations,
	getOrderId,
	getTravellers,
	getHasEverySelectedSeats,
	(skipRecommendations, orderId, travellers, getHasEverySelectedSeats) =>
		!skipRecommendations &&
		// Check if we have something to recommend.
		travellers.some(traveller => traveller.preselectedServices.seats.length)
);

export const getIsNeedToConfirmConsents = createSelector(useConfig, config => {
	return !['A9', 'RM', 'DV', 'JI', '5G', '2S'].includes(config.global.companyInfo.iataCode);
});

interface ServicesByTypes {
	[segmentId: string]: {
		[serviceType: string]: GdsAggregationServiceGroup;
	};
}

const addServiceIntoGroup = (
	servicesByTypes: ServicesByTypes,
	type: GdsAggregationServiceType,
	segmentId: string,
	service: GdsAggregationService,
	timelineStep: TimelineStep,
	canBeAdded: boolean = true,
	selectedServices: GdsAggregationSelectedService[] = []
) => {
	const servicePrice = getServicePrice(service);
	const step = getAggregatorGroupType(type, timelineStep);

	if (servicesByTypes[segmentId]) {
		if (servicesByTypes[segmentId][step]) {
			servicesByTypes[segmentId][step].services.push(service);

			if (servicePrice && servicesByTypes[segmentId][step].minPrice.amount > servicePrice.amount) {
				servicesByTypes[segmentId][step].minPrice.amount = servicePrice.amount;
			}
			if (canBeAdded) {
				servicesByTypes[segmentId][step].canAddService = true;
			}
		} else {
			servicesByTypes[segmentId][step] = {
				type: type,
				services: [service],
				selectedServices: selectedServices,
				totalPrice: {
					amount: 0,
					currency: null
				},
				minPrice: {
					amount: servicePrice?.amount ?? 0,
					currency: servicePrice.currency
				},
				canAddService: canBeAdded,
				step: timelineStep
			};
		}
	}
};

const getRelatedAndSelectedSegmentIdsForService = (
	service: GdsService_OrderAdditionalServiceGdsServiceBaseInfo_Fragment,
	selectedServices: SelectedServicesState
): string[] => {
	const relatedServicesSegments = getRelatedSegments(service.allowedSegments ?? []);

	Object.keys(selectedServices).forEach(passengerId => {
		if (selectedServices[passengerId].hasOwnProperty(service.id)) {
			selectedServices[passengerId][service.id].forEach(selected =>
				selected.segments.forEach(segmentId => relatedServicesSegments.add(segmentId))
			);
		}
	});

	return [...relatedServicesSegments];
};

export const getGdsServicesBySegments = createSelector(
	[
		getOrder,
		getSegments,
		getSelectedServicesState,
		getBrandIncludedBaggage,
		getServices,
		getSeatMap,
		getAggregatorInsurancesByGroups,
		getSelectedInsurances,
		getAeroexpress,
		getEveryPortServices,
		useConfig
	],
	(
		order,
		segments,
		selectedServices,
		brandIncluded,
		gdsServices,
		seatMap,
		insurances,
		selectedInsurances,
		aeroexpress,
		everyPort,
		config
	) => {
		const servicesByTypes: {
			[segmentId: string]: {
				[serviceType: string]: GdsAggregationServiceGroup;
			};
		} = {};

		segments.forEach(segment => {
			servicesByTypes[segment.id] = {};
		});

		const serviceIdTypes = new Map<string, GdsAggregationServiceType>();
		const gdsServiceMap = new Map<string, GdsServiceFragment>();

		gdsServices.forEach(service => {
			gdsServiceMap.set(service.id, service);

			let type: GdsAggregationServiceType = service.type,
				convertedService: GdsAggregationService,
				canBeAdded = true,
				isRecommended = false;

			if (service.type === OrderAdditionalServiceGdsServiceServiceType.Seat) {
				return;
			} else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
				const baggageService = service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;

				if (isPetBaggage(baggageService) && config.global?.servicesAvailable?.animal) {
					type = 'animal';
				}

				convertedService = { ...convertBaggageObject(baggageService), _serviceType: 'gdsService' };
				canBeAdded = service.canBeAdded;
			} else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Meal) {
				convertedService = {
					...convertMealObject(service as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment),
					_serviceType: 'gdsService'
				};
				canBeAdded = service.canBeAdded;
			} else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Vip) {
				const vipService = service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

				if (vipService.subType === GdsServiceVipSubType.BaggageDelivery) {
					type = 'baggageDelivery';
				}

				if (vipService.subType === GdsServiceVipSubType.Upgrade) {
					type = 'upgrade';
				}

				convertedService = { ...vipService, _serviceType: 'gdsService' } as any;
			} else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Misc) {
				const miscService = service as GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

				if (miscService.isRecommended) {
					isRecommended = true;
				}

				convertedService = { ...miscService, _serviceType: 'gdsService' } as any;
			} else {
				convertedService = {
					...(service as GdsService_OrderAdditionalServiceGdsServiceBaseInfo_Fragment),
					_serviceType: 'gdsService'
				} as any;
			}

			const relatedSegments = getRelatedSegments(service.allowedSegments ?? []);

			serviceIdTypes.set(service.id, type);

			getRelatedAndSelectedSegmentIdsForService(service, selectedServices).forEach(segmentId => {
				const timelineStep = service.timelineStep ?? getDefaultTimelineStep(type);

				addServiceIntoGroup(
					servicesByTypes,
					type,
					segmentId,
					convertedService,
					timelineStep,
					relatedSegments.has(segmentId) && canBeAdded
				);

				if (isRecommended && timelineStep !== TimelineStep.Recommended) {
					addServiceIntoGroup(
						servicesByTypes,
						type,
						segmentId,
						convertedService,
						TimelineStep.Recommended,
						relatedSegments.has(segmentId) && canBeAdded
					);
				}
			});
		});

		if (order.ffpInfo?.ffpSupported) {
			segments.forEach(segment => {
				const segmentId = segment.id;
				const loyaltyType: GdsAggregationServiceType = 'loyalty';
				const timelineStep = getDefaultTimelineStep(loyaltyType);

				servicesByTypes[segmentId][loyaltyType] = {
					type: loyaltyType,
					step: timelineStep,
					services: [],
					selectedServices: [],
					canAddService: true
				};
			});
		}

		brandIncluded.forEach(service => {
			service.baggage.includedSegments.forEach(segmentId => {
				if (servicesByTypes[segmentId]?.[OrderAdditionalServiceGdsServiceServiceType.Baggage]) {
					servicesByTypes[segmentId][
						OrderAdditionalServiceGdsServiceServiceType.Baggage
					].selectedServices.push({
						count: service.count,
						type: OrderAdditionalServiceGdsServiceServiceType.Baggage,
						service: {
							...service.baggage,
							_serviceType: 'gdsService'
						}
					});
				}
			});
		});

		seatMap.segments.forEach(segment => {
			const minPrice = {
				amount: Infinity,
				currency: 'RUB'
			};

			segment.decks.forEach(desk => {
				desk.rows.forEach(row =>
					row.seats.forEach(seat => {
						if (seat.isAvailable && minPrice.amount > seat.price.amount) {
							minPrice.amount = seat.price.amount;
							minPrice.currency = seat.price.currency;
						}
					})
				);
			});

			servicesByTypes[segment.segmentsInfo.id][OrderAdditionalServiceGdsServiceServiceType.Seat] = {
				type: OrderAdditionalServiceGdsServiceServiceType.Seat,
				canAddService: segment.availableSeatsCount > 0,
				services: [],
				selectedServices: [],
				minPrice,
				totalPrice: {
					amount: 0,
					currency: null
				},
				step: getDefaultTimelineStep(OrderAdditionalServiceGdsServiceServiceType.Seat)
			};
		});

		if (aeroexpress?.trips?.length) {
			aeroexpress.trips.forEach(trip => {
				trip.segments?.forEach(segment => {
					addServiceIntoGroup(
						servicesByTypes,
						'transfer',
						segment.id,
						{ ...trip, _serviceType: 'aeroexpress' },
						getDefaultTimelineStep('transfer', trip.toStation?.direction)
					);
				});
			});

			aeroexpress.selectedTrips.forEach(selectedTrip => {
				selectedTrip.segments?.forEach(segment => {
					servicesByTypes[segment.id][
						getAggregatorGroupType(
							'transfer',
							getDefaultTimelineStep('transfer', selectedTrip.toStation?.direction)
						)
					].selectedServices?.push({
						count: 1,
						type: 'transfer',
						service: { ...selectedTrip, _serviceType: 'aeroexpress' }
					});
				});
			});
		}

		if (insurances.length) {
			const insuranceTimelineStep = getDefaultTimelineStep('insuranceGroups');
			const selectedInsuranceServices: GdsAggregationSelectedService[] = selectedInsurances.map(selected => ({
				type: 'insuranceGroups',
				count: 1,
				service: {
					_serviceType: 'insurance',
					...selected.insuranceProgram
				}
			}));

			Object.keys(servicesByTypes).forEach(segmentId => {
				servicesByTypes[segmentId][getAggregatorGroupType('insuranceGroups', insuranceTimelineStep)] = {
					type: 'insuranceGroups',
					services: [
						{
							_serviceType: 'insuranceGroups',
							groups: insurances
						}
					],
					selectedServices: selectedInsuranceServices,
					canAddService: true,
					step: insuranceTimelineStep
				};
			});
		}

		if (everyPort?.services) {
			const everyPortSelectedServices: GdsAggregationSelectedService[] =
				everyPort.selectedServices?.map(service => ({
					service: {
						...everyPort.services.find(({ id }) => id === service.product.id),
						_serviceType: 'nas'
					},
					type: service.product.category,
					count: 1
				})) ?? [];

			everyPort.services.forEach(everyPortService => {
				const segmentId = everyPortService.segmentId.toString();

				addServiceIntoGroup(
					servicesByTypes,
					everyPortService.category,
					segmentId,
					{
						...everyPortService,
						_serviceType: 'nas'
					},
					everyPortService.timelineStep ??
						getDefaultTimelineStep(everyPortService.category, everyPortService.direction),
					order.status !== OrderStatus.Cancelled && everyPortService.canBeAdded,
					everyPortSelectedServices
				);
			});
		}

		for (const services of Object.values(selectedServices)) {
			for (const [serviceId, selectedServices] of Object.entries(services)) {
				const gdsService = gdsServiceMap.get(serviceId);
				const cachedServiceType = serviceIdTypes.get(serviceId);

				for (const selectedService of selectedServices) {
					const { servicePayload } = selectedService;
					if (!servicePayload?.type) {
						continue;
					}

					const type = cachedServiceType ?? servicePayload.type;
					const timelineStep = gdsService?.timelineStep ?? getDefaultTimelineStep(type);
					const serviceType = getAggregatorGroupType(type, timelineStep);

					const isSeatServiceType = type === OrderAdditionalServiceGdsServiceServiceType.Seat;

					for (const segmentId of selectedService.segments) {
						const segmentServices =
							servicesByTypes[segmentId]?.[serviceType] ?? servicesByTypes[segmentId]?.[type];

						if (!segmentServices) {
							continue;
						}

						const gdsAggregationService = isSeatServiceType
							? ({ ...servicePayload, _serviceType: 'seat' } as GdsAggregationServiceSeat)
							: segmentServices.services?.find(s => 'id' in s && s.id === serviceId);

						segmentServices.selectedServices.push({
							type,
							count: selectedService.number,
							service: gdsAggregationService
						});

						const servicePrice = getServicePrice(gdsAggregationService);
						if (servicePrice) {
							segmentServices.totalPrice = addMoney(segmentServices.totalPrice, servicePrice);
						}
					}
				}
			}
		}

		const aggregatedServices: GdsServicesAggregations[] = [];

		Object.keys(servicesByTypes).forEach(segmentId => {
			const services: GdsAggregationServiceGroup[] = [];

			Object.keys(servicesByTypes[segmentId]).forEach(type => {
				services.push(servicesByTypes[segmentId][type]);
			});

			aggregatedServices.push({
				segmentId,
				services
			});
		});

		return aggregatedServices;
	}
);

export const getTravellersValueNames = createSelector(getTravellers, travellers => {
	const map: Map<TravellerFieldEnum, string> = new Map();

	if (travellers?.length !== 0) {
		travellers[0].values.forEach(v => {
			map.set(v.type, v.name);
		});
	}

	return map;
});

export const getHasRequestedOrderServices = createSelector(
	getOrder,
	getIsBookedOrConfirmed,
	getTravellers,
	(order, isBookedOrConfirmed, travellers) => {
		return isBookedOrConfirmed && getHasRequestedServices(travellers, order.ssrData);
	}
);

export const getExistGdsServicesOrInsurances = createSelector(getServices, getInsurances, (gdsServices, insurances) => {
	return gdsServices.length > 0 || insurances?.insurancePrograms?.length > 0;
});

export const getSplittedOrdersIds = createSelector(getOrder, order => order.splittedOrdersIds);

export const getIsSplittedOrder = createSelector(
	getSplittedOrdersIds,
	splittedOrdersIds => !!splittedOrdersIds?.length
);
